/**
 * MessageList Component
 *
 * Display chat messages with role-based styling
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Bot, User, Settings } from 'lucide-react';
import type { ACFMessage } from '@/hooks/useACF';

interface MessageListProps {
  messages: ACFMessage[];
  streamingContent?: string;
  isStreaming?: boolean;
  className?: string;
}

/**
 * Format message timestamp
 */
function formatTime(date: string): string {
  return new Date(date).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get role icon
 */
function getRoleIcon(role: ACFMessage['role']): React.ReactNode {
  switch (role) {
    case 'user':
      return <User className="h-4 w-4" />;
    case 'assistant':
      return <Bot className="h-4 w-4" />;
    case 'system':
      return <Settings className="h-4 w-4" />;
  }
}

/**
 * Get role color
 */
function getRoleColor(role: ACFMessage['role']): string {
  switch (role) {
    case 'user':
      return 'bg-primary text-primary-foreground';
    case 'assistant':
      return 'bg-secondary text-secondary-foreground';
    case 'system':
      return 'bg-muted text-muted-foreground';
  }
}

/**
 * MessageBubble - Single message display
 */
function MessageBubble({
  message,
  isStreaming = false,
}: {
  message: ACFMessage | { role: 'assistant'; content: string; id?: string };
  isStreaming?: boolean;
}): React.JSX.Element {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';

  return (
    <div
      className={cn(
        'flex gap-3 p-4 rounded-lg',
        isUser && 'bg-primary/5 ml-8',
        !isUser && !isSystem && 'bg-secondary/50 mr-8',
        isSystem && 'bg-muted/50 mx-4 text-sm'
      )}
    >
      <Avatar className={cn('h-8 w-8', getRoleColor(message.role))}>
        <AvatarFallback className="bg-transparent">
          {getRoleIcon(message.role)}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm capitalize">{message.role}</span>
          {'createdAt' in message && (
            <span className="text-xs text-muted-foreground">
              {formatTime(message.createdAt)}
            </span>
          )}
          {'model' in message && message.model && (
            <Badge variant="outline" className="text-xs">
              {message.model}
            </Badge>
          )}
          {isStreaming && (
            <Badge variant="secondary" className="text-xs animate-pulse">
              Streaming...
            </Badge>
          )}
        </div>

        <div className="prose prose-sm max-w-none dark:prose-invert">
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
            {message.content}
            {isStreaming && (
              <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-0.5" />
            )}
          </pre>
        </div>

        {'tokenCount' in message && message.tokenCount && (
          <div className="mt-2 text-xs text-muted-foreground">
            {message.tokenCount} tokens
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * MessageList - Display all messages in a session
 */
export function MessageList({
  messages,
  streamingContent,
  isStreaming,
  className,
}: MessageListProps): React.JSX.Element {
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  if (messages.length === 0 && !isStreaming) {
    return (
      <div className={cn('flex flex-col items-center justify-center h-full text-center p-8', className)}>
        <Bot className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">Start a Conversation</h3>
        <p className="text-muted-foreground max-w-sm">
          Send a message to begin your AI coding session. You can ask questions,
          get code suggestions, or discuss your project.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-4 p-4', className)}>
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} />
      ))}

      {/* Streaming message */}
      {isStreaming && streamingContent && (
        <MessageBubble
          message={{
            role: 'assistant',
            content: streamingContent,
          }}
          isStreaming
        />
      )}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />
    </div>
  );
}
